/**
 * Shared types between the Cloudflare Worker (api) and the web client.
 */

/** Normalized aircraft state. Produced by the provider layer in the Worker. */
export interface Aircraft {
  /** 24-bit ICAO transponder address, lowercase hex (e.g. "4ca87c"). */
  icao: string;
  /** Trimmed callsign, e.g. "UAL123", or null when not received. */
  callsign: string | null;
  /** Civil registration, e.g. "N12345", or null. */
  registration: string | null;
  /** ICAO aircraft type designator from the upstream `t` field, e.g. "A320", "B738", or null. */
  type: string | null;
  /** Human-readable type from upstream `desc`, e.g. "EMBRAER ERJ-145", or null. */
  description: string | null;
  /** Owner/operator from upstream `ownOp`, e.g. "AMERICAN AIRLINES INC", or null. */
  operator: string | null;
  /** ADS-B emitter category description when available (e.g. "A2"). */
  category: string | null;
  lat: number | null;
  lon: number | null;
  /** Barometric altitude in feet, or null. */
  altBaro: number | null;
  /** Geometric altitude in feet, or null. */
  altGeo: number | null;
  /** Ground speed in knots, or null. */
  groundSpeed: number | null;
  /** True track / heading in degrees (0 = north), or null. */
  track: number | null;
  /** Vertical rate in ft/min (positive = climbing), or null. */
  verticalRate: number | null;
  /** Transponder squawk code, e.g. "7700", or null. */
  squawk: string | null;
  /** Emergency state string ("none", "general", "lifeguard", "nordo", ...), or null. */
  emergency: string | null;
  onGround: boolean;
  /** Inferred origin country, or null. */
  originCountry: string | null;
  /** Military-flagged by the provider (authoritative) or by reference data. */
  isMilitary: boolean;
  /** Seconds since the aircraft was last seen by a receiver. */
  seen: number;
  /** Source provider that produced this record. */
  source: 'airplaneslive' | 'adsblol' | 'adsbfi';
}

/** Geographic bounding box: west, south, east, north. */
export interface BBox {
  minLon: number;
  minLat: number;
  maxLon: number;
  maxLat: number;
}

/** Request for live states. */
export interface StatesRequest {
  bbox: BBox;
  /** Optional single global filter that lets the Worker hit a dedicated endpoint. */
  globalFilter?: 'mil' | { type: string } | null;
}

/** Response from /api/states. */
export interface StatesResponse {
  /** Server unix timestamp (seconds) the snapshot was assembled. */
  time: number;
  aircraft: Aircraft[];
  /** When set, the view is too wide; tell the client to zoom in. */
  zoomInToLoad?: boolean;
  /** Some tiles are still being refreshed or the viewport exceeded the configured tile cap. */
  partial?: boolean;
  /** Number of live-data requests/slices used to cover this viewport. */
  tileCount?: number;
  /** Requests/slices returned from stale/no cache and scheduled for background refresh. */
  warmingTiles?: number;
  /** Whether the response was served fully from cache (no upstream calls). */
  fromCache: boolean;
  /** Upstream providers actually contacted (for debugging/metrics). */
  providersHit: Array<'airplaneslive' | 'adsblol' | 'adsbfi'>;
  /** Why a live fetch returned no aircraft — never silently empty on provider failure. */
  providerErrors?: string[];
}

/** One historical position for a selected aircraft trace. */
export interface FlightTracePoint {
  lat: number;
  lon: number;
  /** Unix timestamp in seconds. */
  t: number;
  altBaro: number | null;
  groundSpeed: number | null;
  track: number | null;
}

/** Response from /api/trace/:icao. */
export interface FlightTraceResponse {
  icao: string;
  callsign: string | null;
  registration: string | null;
  type: string | null;
  points: FlightTracePoint[];
  fromCache: boolean;
  source: 'adsblol';
}

/** Airport reference used for takeoff / destination display. */
export interface FlightAirport {
  icao: string;
  iata: string | null;
  name: string | null;
  location: string | null;
}

/** Response from /api/flight/:icao. */
export interface FlightPlanResponse {
  icao: string;
  callsign: string | null;
  departure: FlightAirport | null;
  arrival: FlightAirport | null;
  /** Scheduled route matched from callsign data. */
  routeKnown: boolean;
  /** Takeoff airport inferred from the current-flight trace when route data is missing. */
  departureInferred: boolean;
  fromCache: boolean;
}

/** Aircraft metadata resolved from bundled reference data + provider lookups. */
export interface AircraftMetadata {
  icao: string;
  registration: string | null;
  type: string | null;
  /** Resolved type info from bundled aircraft-types.json, if known. */
  typeInfo: AircraftTypeInfo | null;
  /** Resolved airline from callsign prefix, if known. */
  airline: AirlineInfo | null;
  operator: string | null;
  isMilitary: boolean;
}

/** A row in the bundled aircraft-types.json reference dataset. */
export interface AircraftTypeInfo {
  /** ICAO type designator, e.g. "A320". */
  icao: string;
  manufacturer: string;
  model: string;
  /** Broad category: "commercial_jet", "turboprop", "private_jet", "regional", "helicopter", "military", "glider", "light", "other". */
  category: AircraftCategory;
  engines: string | null;
  isMilitary: boolean;
}

/** A row in the bundled airlines.json reference dataset. */
export interface AirlineInfo {
  /** ICAO airline code (3 letters), e.g. "UAL". */
  icao: string;
  iata: string | null;
  name: string;
  country: string | null;
  callsignPrefix: string | null;
  isMilitary: boolean;
}

export type AircraftCategory =
  | 'commercial_jet'
  | 'turboprop'
  | 'private_jet'
  | 'regional'
  | 'helicopter'
  | 'military'
  | 'glider'
  | 'light'
  | 'other';

/** Where the panel photo URL came from. */
export type AircraftPhotoSource =
  | 'planespotters'
  | 'planespotters-reg'
  | 'planespotters-type'
  | 'jetphotos'
  | 'type'
  | 'none';

/** Photo metadata returned by /api/photo/:icao (multi-source waterfall). */
export interface AircraftPhoto {
  icao: string;
  registration: string | null;
  photographer: string | null;
  thumbnailUrl: string | null;
  fullUrl: string | null;
  found: boolean;
  source: AircraftPhotoSource;
  /** True when the image is a generic type photo, not this exact airframe. */
  representative?: boolean;
  /** Human-readable type label for representative photos, e.g. "Northrop T-38 Talon". */
  label?: string | null;
  /** Attribution text (required for Wikimedia-sourced type photos). */
  attribution?: string | null;
}

/** Client-side filter definition. Persisted in localStorage. */
export interface FlightFilter {
  /** Free text over callsign / registration / icao. */
  query: string;
  /** ICAO airline codes to include (empty = any). */
  airlines: string[];
  /** ICAO type codes to include (empty = any). */
  types: string[];
  /** Operator classes to include. */
  classes: FlightClass[];
  /** Detailed visual/icon categories to include (empty = any). */
  iconKinds: AircraftIconKind[];
  /** Altitude band in feet. */
  altMin: number | null;
  altMax: number | null;
  /** Ground speed band in knots. */
  speedMin: number | null;
  speedMax: number | null;
  /** Origin country names (empty = any). */
  countries: string[];
  /** Only show squawk/emergency flights. */
  emergencyOnly: boolean;
  /** null = any, true = on ground, false = airborne. */
  onGround: boolean | null;
}

export type FlightClass = 'commercial' | 'military' | 'private' | 'government';

export type AircraftIconKind =
  | 'presidential'
  | 'government'
  | 'emergency'
  | 'military-fighter'
  | 'military-transport'
  | 'military-other'
  | 'military-helicopter'
  | 'helicopter'
  | 'balloon'
  | 'heavy-commercial'
  | 'commercial'
  | 'regional'
  | 'turboprop'
  | 'private-jet'
  | 'light-prop'
  | 'glider'
  | 'unknown';

export type SpecialFlightKind = 'presidential' | 'vip' | null;

export const DEFAULT_FILTER: FlightFilter = {
  query: '',
  airlines: [],
  types: [],
  classes: [],
  iconKinds: [],
  altMin: null,
  altMax: null,
  speedMin: null,
  speedMax: null,
  countries: [],
  emergencyOnly: false,
  onGround: null,
};
